import { lazy, Suspense, useState } from "react";
import { Search, Calendar as CalendarIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

const Calendar = lazy(() =>
  import("@/components/ui/calendar").then((m) => ({ default: m.Calendar }))
);

interface HeroSectionProps {
  searchQuery?: string;
  onSearchChange?: (query: string) => void;
  onSearch?: () => void;
}

export function HeroSection({ searchQuery = "", onSearchChange, onSearch }: HeroSectionProps) {
  const [date, setDate] = useState<Date>();

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && onSearch) {
      onSearch();
    }
  };

  return (
    <div className="relative overflow-hidden">
      {/* Background Image */}
      <img
        src="https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1920&q=75&fm=webp"
        srcSet="https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=640&q=75&fm=webp 640w, https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1024&q=75&fm=webp 1024w, https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1920&q=75&fm=webp 1920w"
        sizes="100vw"
        alt="Scenic mountain vista in Varkala"
        fetchPriority="high"
        loading="eager"
        decoding="async"
        className="absolute inset-0 h-full w-full object-cover"
      />
      <div className="hero-gradient absolute inset-0" />

      {/* Content */}
      <div className="container relative z-10 py-24 md:py-32">
        <div className="mx-auto max-w-3xl text-center">
          <h1 className="font-display text-4xl font-bold tracking-tight text-white md:text-5xl lg:text-6xl">
            Find Your Flow in Varkala
          </h1>
          <p className="mt-4 text-lg text-white/80">
            Curated experiences from cliff-side yoga to sunset surfing.
          </p>

          {/* Search Bar */}
          <div className="mx-auto mt-8 flex max-w-2xl flex-col gap-2 rounded-2xl bg-background p-2 shadow-xl sm:flex-row">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
              <Input
                type="text"
                placeholder="What are you looking for?"
                className="border-0 pl-10 shadow-none focus-visible:ring-0"
                value={searchQuery}
                onChange={(e) => onSearchChange?.(e.target.value)}
                onKeyDown={handleKeyDown}
              />
            </div>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  className={cn(
                    "flex-1 justify-start text-left font-normal border-0 shadow-none",
                    !date && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-5 w-5" />
                  {date
                    ? date.toLocaleDateString("en-US", {
                        month: "long",
                        day: "numeric",
                        year: "numeric",
                      })
                    : "Add dates"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Suspense fallback={<div className="p-8" />}>
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={setDate}
                    disabled={(date) => date < new Date()}
                    initialFocus
                  />
                </Suspense>
              </PopoverContent>
            </Popover>
            <Button className="gap-2 px-6" onClick={onSearch}>
              <Search className="h-4 w-4" />
              Search
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
